import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import attachImage from 'assets/svg/attach-icon.svg';
import sendImage from 'assets/svg/send-icon.svg';
import Styles from 'components/chatContent/chatFooter/ChatFooter.module.scss';
import { randomString, getCurrentDate } from 'helpers/utils';
import { sendFCMMessage, sendThreadMessage, updateNewRoomStatus, uploadImage } from 'store/services/firebase-broadcast';
import {
  clearRoomTyping,
  setRoomTyping,
  TYPING_IDLE_MS,
  TYPING_THROTTLE_MS,
} from 'store/services/typingPresence';
import { Attachment } from 'modules/types/types';
import moment from 'moment';
import { invitePeople, invitePeopleRequest } from 'store/actions/general.actions';
import { connect } from 'react-redux';
import { isPhoneViewport } from 'helpers/viewportBreakpoints';

const COMPOSER_MIN_DESKTOP = 54;
const COMPOSER_MIN_PHONE = 44;
const COMPOSER_PAD_V_DESKTOP = 15;
const COMPOSER_PAD_V_PHONE = 10;
const COMPOSER_LINE_HEIGHT = 24;
/** Matches resize math: lineHeight * 2 + vertical padding. */
const COMPOSER_MAX_DESKTOP = 88;
const COMPOSER_MAX_PHONE = 68; // 24*2 + 10+10

const getComposerMinHeight = () =>
  isPhoneViewport() ? COMPOSER_MIN_PHONE : COMPOSER_MIN_DESKTOP;

const getComposerPadV = () =>
  isPhoneViewport() ? COMPOSER_PAD_V_PHONE : COMPOSER_PAD_V_DESKTOP;

const getComposerMaxHeight = () =>
  isPhoneViewport() ? COMPOSER_MAX_PHONE : COMPOSER_MAX_DESKTOP;

interface ChatFooterProps {
  selected: any;
  chats?: any;
  isThread?: boolean;
  parentMessage?: object;
  images?: Function;
  clearImages?: Function;
  activateRoom?: any;
  invitePeople: Function;
  attachedImages?: any;
  onMessageSent?: () => void;
  /** Fired when the user starts typing (dismiss Discord-style NEW divider). */
  onComposerInteract?: () => void;
  /** Phone layout: reports footer outer height so typing label / scroll clearance stay flush. */
  onHeightChange?: (height: number) => void;
}

const ChatFooter: FC<ChatFooterProps> = (props: ChatFooterProps) => {
  const {
    selected,
    chats,
    isThread,
    parentMessage,
    clearImages,
    activateRoom,
    invitePeople,
    attachedImages,
    onMessageSent,
    onComposerInteract,
    onHeightChange,
  } = props;
  const [files, setFiles] = useState<Attachment[]>([]);
  const [message, setMessage] = useState("");
  const [send, setSend] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(() => getComposerMinHeight());
  const [footerBottom, setFooterBottom] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  // Per-file upload progress (0–100), keyed by the file's id.
  const [progressById, setProgressById] = useState<Record<string, number>>({});
  const composerInteractedRef = useRef(false);
  const inputRef: any = useRef(null);
  const textInputRef: any = useRef(null);
  const chatFooterRef: any = useRef(null);
  const typingIdleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPublishingTypingRef = useRef(false);
  const lastTypingPublishAtRef = useRef(0);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [sender] = useState({
    id: user?.pk,
    name: user.name,
    image: user.profile,
    isHost: user?.pk === selected.host?.pk,
    isSuperHost: user?.pk === selected.host?.pk && Boolean(user?.is_super_host),
  });

  const threadId = isThread ? (parentMessage as { id?: string } | undefined)?.id : null;

  const publishTyping = useCallback(() => {
    if (!selected?.pk || !user?.pk || !user?.name) return;
    setRoomTyping(selected.pk, user.pk, user.name, threadId).catch((error) => {
      console.warn('Failed to publish typing status:', error);
    });
  }, [selected?.pk, user?.pk, user?.name, threadId]);

  const stopTyping = useCallback(() => {
    if (typingIdleRef.current) {
      clearTimeout(typingIdleRef.current);
      typingIdleRef.current = null;
    }
    lastTypingPublishAtRef.current = 0;
    if (!isPublishingTypingRef.current) return;
    isPublishingTypingRef.current = false;
    if (!selected?.pk || !user?.pk) return;
    clearRoomTyping(selected.pk, user.pk).catch((error) => {
      console.warn('Failed to clear typing status:', error);
    });
  }, [selected?.pk, user?.pk]);

  const clearAttachments = () => {
    setFiles([]);
    setProgressById({});
    if (inputRef.current) inputRef.current.value = '';
    if (clearImages && typeof clearImages === 'function') clearImages();
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleMessage = async () => {
    if (isUploading) return; // prevent double-send while an upload is in flight
    if (isThread) {
      await sendThread();
    } else {
      await sendMessage();
      const roomForStatus = activateRoom ?? selected;
      if (roomForStatus?.pk != null) {
        updateNewRoomStatus({
          room: roomForStatus,
          activeRoom: moment().format(),
        }).catch((error) => {
          console.warn('Failed to update room active status:', error);
        });
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (message.trim().length > 0 || files.length > 0) {
        handleMessage();
      }
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      const cursorPosition = e.currentTarget.selectionStart;
      if (cursorPosition !== null) {
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        const newMessage = textBeforeCursor + '\n' + textAfterCursor;
        setMessage(newMessage);
        
        // Set cursor position after the new line using a more reliable approach
        setTimeout(() => {
          const inputElement = textInputRef.current;
          if (inputElement && inputElement.setSelectionRange) {
            try {
              inputElement.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
              inputElement.focus();
            } catch (error) {
              console.warn('Failed to set cursor position:', error);
            }
          }
        }, 10);
      }
    }
  };

  const handleAttachment = (e: any) => {
    const picked: FileList = e.target.files;
    if (picked && picked.length > 0) {
      const newFiles: Attachment[] = Array.from(picked).map((f: File) => ({
        id: `${selected?.pk}-${randomString()}`,
        name: f.name,
        path: URL.createObjectURL(f),
        file: f,
        type: (f.type || '').split('/')[0] === 'video' ? 'video' : 'image',
      }));
      // Append so users can add more before sending.
      setFiles((prev) => [...prev, ...newFiles]);
      // Reset the input so picking the same file again still fires onChange.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Upload one file and send it as its own single-attachment message. Text (if any)
  // rides along as the caption on the first file only. `extra` carries the thread
  // parentId for reply sends. Reports per-file progress.
  const uploadAndSendFile = async (
    f: Attachment,
    caption: string,
    send: (msg: any) => Promise<any>,
    extra: object = {}
  ) => {
    const msg: any = {
      ...extra,
      roomId: selected?.pk,
      id: `${selected?.pk}-${randomString()}`,
      sender: sender,
      receiver: {},
      message: caption,
      time: getCurrentDate(),
      is_delete: false,
      attachments: [{ id: f.id, name: f.name, type: (f as any).type || 'image' }],
      threads: [],
    };
    await uploadImage(msg, f, (p) => setProgressById((prev) => ({ ...prev, [f.id]: p })));
    await send(msg);
  };

  const sendTextMessage = async (
    send: (msg: any) => Promise<any>,
    draftText: string,
    extra: object = {}
  ) => {
    const msg: any = {
      ...extra,
      roomId: selected?.pk,
      id: `${selected?.pk}-${randomString()}`,
      sender: sender,
      receiver: {},
      message: draftText,
      time: getCurrentDate(),
      is_delete: false,
      attachments: [],
      threads: [],
    };
    await send(msg);
  };

  // Shared send flow for both the main footer and thread replies: fan out one
  // message per selected file (uploads run concurrently), text becomes the first
  // file's caption, or a standalone text message when nothing is attached.
  // Text clears immediately; attachment chips + progress stay until uploads finish.
  const runSend = async (send: (msg: any) => Promise<any>, extra: object = {}) => {
    if (isUploading) return;

    const hasFiles = files.length > 0;
    const text = message.trim();
    if (!hasFiles && !text) return;

    const draftText = message;
    const draftFiles = [...files];

    // Drop typing + clear text immediately so network latency doesn't leave the
    // draft visible. Keep attachment chips until uploads complete so progress UI works.
    stopTyping();
    const minH = getComposerMinHeight();
    setMessage('');
    setTextareaHeight(minH);
    if (textInputRef.current) {
      textInputRef.current.style.height = `${minH}px`;
      textInputRef.current.style.overflowY = 'hidden';
    }

    try {
      if (hasFiles) {
        setIsUploading(true);
        // Concurrent uploads; one failure doesn't abort the others.
        await Promise.allSettled(
          draftFiles.map((f, i) =>
            uploadAndSendFile(f, i === 0 ? draftText : '', send, extra)
          )
        );
        clearAttachments();
      } else {
        await sendTextMessage(send, draftText, extra);
      }

      onMessageSent && onMessageSent();
    } catch (error: any) {
      if (error?.code === 'INVALID_USER_DATA') return;
      console.error('Failed to send message:', error);
      // Restore text draft so the user can retry after a hard failure.
      // Attachments were never cleared until success, so they remain in the UI.
      setMessage(draftText);
    } finally {
      setIsUploading(false);
      setProgressById({});
    }
  };

  const sendMessage = async () => {
    await runSend(sendFCMMessage);
  };

  const sendThread = async () => {
    const { id }: any = parentMessage;
    await runSend(sendThreadMessage, { parentId: id });
  };

  const parentMessageId = (parentMessage as { id?: string } | undefined)?.id;

  useEffect(() => {
    composerInteractedRef.current = false;
  }, [selected?.pk, parentMessageId, isThread]);

  // Leading-edge typing: publish on the first keystroke immediately so peers
  // see the indicator during a fast burst, then throttle refreshes while typing
  // continues. Clear after TYPING_IDLE_MS of silence (even with draft text left).
  useEffect(() => {
    if (message.trim().length > 0) {
      setSend(true);

      if (typingIdleRef.current) {
        clearTimeout(typingIdleRef.current);
        typingIdleRef.current = null;
      }
      typingIdleRef.current = setTimeout(() => {
        stopTyping();
      }, TYPING_IDLE_MS);

      const now = Date.now();
      const shouldPublish =
        !isPublishingTypingRef.current ||
        now - lastTypingPublishAtRef.current >= TYPING_THROTTLE_MS;

      if (shouldPublish) {
        isPublishingTypingRef.current = true;
        lastTypingPublishAtRef.current = now;
        publishTyping();
      }
    } else {
      setSend(false);
      stopTyping();
    }
  }, [message, publishTyping, stopTyping]);

  // Clear on room change / unmount.
  useEffect(() => () => {
    stopTyping();
  }, [stopTyping]);

  useEffect(() => {
    // Add null check for attachedImages before calling filter
    if (attachedImages && Array.isArray(attachedImages)) {
      const noFile = attachedImages.filter((i:any) => i.file === inputRef?.current?.files[0]);
      if (noFile.length === 0) {
        inputRef.current.value = '';
      }
    }
  }, [attachedImages])

  // Handle mobile keyboard visibility with Visual Viewport API
  useEffect(() => {
    // Only run on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    if (!isMobile || !window.visualViewport) return;

    const handleViewportChange = () => {
      const viewport = window.visualViewport;
      if (!viewport) return;
      
      const windowHeight = window.innerHeight;
      const viewportHeight = viewport.height;
      
      // Calculate how much the keyboard is taking up
      const keyboardHeight = windowHeight - viewportHeight;
      
      // Adjust footer position to stay above keyboard
      if (keyboardHeight > 100) {
        // Keyboard is showing
        setFooterBottom(keyboardHeight);
        
        // Scroll textarea into view after a small delay
        setTimeout(() => {
          if (textInputRef.current && document.activeElement === textInputRef.current) {
            textInputRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 100);
      } else {
        // Keyboard is hidden
        setFooterBottom(0);
      }
    };

    // Listen to both resize and scroll events
    window.visualViewport.addEventListener('resize', handleViewportChange);
    window.visualViewport.addEventListener('scroll', handleViewportChange);

    // Initial check
    handleViewportChange();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
      window.visualViewport?.removeEventListener('scroll', handleViewportChange);
      document.documentElement.style.removeProperty('--chat-keyboard-offset');
    };
  }, []);

  // Keep typing label (and anything else using this var) aligned with the lifted footer
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--chat-keyboard-offset',
      footerBottom > 0 ? `${footerBottom}px` : '0px'
    );
    return () => {
      document.documentElement.style.removeProperty('--chat-keyboard-offset');
    };
  }, [footerBottom]);

  // Report outer height (padding included) so phone typing bar / scroll inset track the real composer.
  useEffect(() => {
    const el = chatFooterRef.current as HTMLElement | null;
    if (!el || !onHeightChange) return;

    const report = () => {
      onHeightChange(Math.round(el.getBoundingClientRect().height));
    };

    report();
    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange, files.length, textareaHeight]);

  // Scroll textarea into view when focused
  useEffect(() => {
    const textarea = textInputRef.current;
    if (!textarea) return;

    const handleFocus = () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
      if (!isMobile) return;

      // Multiple scroll attempts to handle various keyboard opening speeds
      const scrollIntoView = () => {
        textarea.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      };

      scrollIntoView();
      setTimeout(scrollIntoView, 300);
      setTimeout(scrollIntoView, 600);
    };

    textarea.addEventListener('focus', handleFocus);

    return () => {
      textarea.removeEventListener('focus', handleFocus);
    };
  }, []);
  
  // Scroll textarea so the cursor stays visible when content overflows
  const scrollCursorIntoView = (textarea: HTMLTextAreaElement) => {
    const cursorPos = textarea.selectionStart;
    if (cursorPos === null) return;

    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 24;
    const paddingTop = parseFloat(style.paddingTop) || 8;

    // Use a mirror div off-screen to get the pixel offset of the cursor from the top of the content
    const mirror = document.createElement('div');
    mirror.style.cssText = `
      position: fixed; left: -9999px; top: 0; visibility: hidden; white-space: pre-wrap; word-wrap: break-word;
      font: ${style.font}; padding: ${style.padding}; width: ${textarea.clientWidth}px;
      box-sizing: border-box; line-height: ${style.lineHeight};
    `;
    mirror.textContent = textarea.value.substring(0, cursorPos);
    document.body.appendChild(mirror);
    const cursorOffsetFromTop = mirror.offsetHeight - paddingTop;
    document.body.removeChild(mirror);

    const visibleTop = textarea.scrollTop;
    const visibleBottom = textarea.scrollTop + textarea.clientHeight;
    const cursorLineTop = cursorOffsetFromTop;
    const cursorLineBottom = cursorOffsetFromTop + lineHeight;

    if (cursorLineBottom > visibleBottom) {
      textarea.scrollTop = cursorLineBottom - textarea.clientHeight + paddingTop;
    } else if (cursorLineTop < visibleTop) {
      textarea.scrollTop = Math.max(0, cursorLineTop - paddingTop);
    }
  };

  // Auto-resize textarea based on content with better height management
  useEffect(() => {
    if (textInputRef.current) {
      const textarea = textInputRef.current;
      
      // Get computed styles to calculate accurate line height
      const computedStyle = window.getComputedStyle(textarea);
      // Get line-height - prefer inline style value (24px) for consistency
      let lineHeight = parseFloat(computedStyle.lineHeight);
      // If line-height seems too small (likely from CSS override), use explicit 24px
      if (isNaN(lineHeight) || lineHeight < 20) {
        lineHeight = 24; // Explicit 24px as set in inline style
      }
      
      const paddingTop = parseFloat(computedStyle.paddingTop) || 8;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 8;
      
      const maxLines = 2; // Max 3 lines
      // Calculate max height for exactly 3 lines: line height * maxLines + top padding + bottom padding
      // Use Math.floor to ensure we never exceed 3 lines
      const maxHeight = Math.floor((lineHeight * maxLines) + paddingTop + paddingBottom);
      
      // Set max-height FIRST to prevent rendering beyond 3 lines
      textarea.style.maxHeight = `${maxHeight}px`;
      
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate new height with constraints (max ~3 lines, but at least minHeight)
      const minHeight = getComposerMinHeight();
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      
      // Update state for height tracking
      setTextareaHeight(newHeight);
      
      // ALWAYS enforce maxHeight - never allow height to exceed 3 lines
      const finalHeight = Math.min(newHeight, maxHeight);
      textarea.style.height = `${finalHeight}px`;
      
      // Show scrollbar when content exceeds max height (3 lines)
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
        // CRITICAL: Force height to exactly maxHeight when scrolling
        textarea.style.height = `${maxHeight}px`;
        textarea.style.maxHeight = `${maxHeight}px`;
      } else {
        textarea.style.overflowY = 'hidden';
      }
      textarea.style.overflowX = 'hidden';
      
      // Ensure the parent container adjusts smoothly
      if (textarea.parentElement) {
        textarea.parentElement.style.transition = 'height 0.2s ease';
      }

      // Keep cursor in view when textarea content scrolls
      scrollCursorIntoView(textarea);
      // Run again after a short delay (e.g. after Shift+Enter sets cursor in setTimeout)
      const t = setTimeout(() => {
        if (textInputRef.current) scrollCursorIntoView(textInputRef.current);
      }, 25);
      return () => clearTimeout(t);
    }
  }, [message]);
  return (
    <div 
      ref={chatFooterRef}
      className={`${Styles.ChatFooter}`} 
      style={{ 
        bottom: footerBottom > 0 ? `${footerBottom}px` : undefined 
      }}
    >
      <div className={`${Styles.messageBox} position-relative`} style={{
        minHeight: `${getComposerMinHeight()}px`,
        transition: 'height 0.2s ease'
      }}>
        {/* Selected attachment previews — one chip per file (thumbnail + name),
            each with its own upload progress bar. */}
        {files.length > 0 && (
          <div className={Styles.attachmentStrip}>
            {files.map((f) => (
              <div className={Styles.attachmentChip} key={f.id}>
                <div className={Styles.chipRow}>
                  {(f as any).type === 'video' ? (
                    <video className={Styles.chipThumb} src={f.path} muted />
                  ) : (
                    <img className={Styles.chipThumb} src={f.path} alt="" />
                  )}
                  <span className={Styles.chipName} title={f.name}>{f.name}</span>
                  {!isUploading && (
                    <button
                      type="button"
                      className={Styles.chipRemove}
                      onClick={() => removeFile(f.id)}
                      aria-label="Remove attachment"
                    >
                      ×
                    </button>
                  )}
                </div>
                {isUploading && (
                  <div className={Styles.chipProgress}>
                    <div style={{ width: `${progressById[f.id] || 0}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={Styles.inputRow} style={{ height: `${textareaHeight}px` }}>
          {/* Text input area */}
          <div style={{ position: 'relative', flex: 1, minWidth: 0, height: '100%' }}>
            <textarea
              ref={textInputRef}
              value={message}
              placeholder="Type your message…"
              className={`w-100 ${Styles.typingArea}`}
              onChange={(e) => {
                const next = e.target.value;
                setMessage(next);
                if (!composerInteractedRef.current && next.trim().length > 0) {
                  composerInteractedRef.current = true;
                  onComposerInteract?.();
                }
                setTimeout(() => {
                  if (textInputRef.current) scrollCursorIntoView(textInputRef.current);
                }, 0);
              }}
              onKeyDown={handleKeyDown}
              rows={1}
              style={{
                resize: 'none',
                overflow: 'hidden',
                overflowX: 'hidden',
                minHeight: `${getComposerMinHeight()}px`,
                maxHeight: `${getComposerMaxHeight()}px`,
                lineHeight: `${COMPOSER_LINE_HEIGHT}px`,
                padding: `${getComposerPadV()}px 12px`,
                transition: 'height 0.2s ease',
                boxSizing: 'border-box',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                position: 'relative',
                zIndex: 1,
              }}
            />
          </div>

          {/* Attach + send grouped at the right, inside the box (Figma) */}
          <div className={Styles.attachButtonWrapper}>
            <div className={`position-relative ${Styles.attachButton}`}>
              <input ref={inputRef} type="file" multiple accept="image/*,video/*" onChange={handleAttachment} />
              <img src={attachImage} alt="attach file" />
            </div>
          </div>

          {send || files.length > 0 ? (
            <div className={Styles.sendButton} onClick={handleMessage} onKeyPress={handleMessage} role="button" tabIndex={0}>
              <img src={sendImage} className={Styles.sendIcon} alt="send icon" />
            </div>
          ) : (
            <div className={Styles.sendButton}>
              <img src={sendImage} className={Styles.sendIcon} alt="send icon" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const mapDispatchToProps = { invitePeople: invitePeopleRequest };
export default connect(null, mapDispatchToProps)(ChatFooter);
